import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { FilterFnOption } from '@tanstack/react-table';

import { identifierToKey } from '@/utils/hooks/useManagedDatagrid';

import { DatagridIdentifier, ManagedDatagridState, Row } from '@/types/DataGrid';

interface DatagridState
{
	datagrids: Record<string, ManagedDatagridState>;
}

const initialState: DatagridState = {
	datagrids: {}
};

export const datagridSlice = createSlice({
	name: 'datagrid',
	initialState,
	reducers: {
		setDatagrid: (
			state,
			action: PayloadAction<{
				identifier: DatagridIdentifier | string;
				state: ManagedDatagridState;
			}>
		) =>
		{
			const key = typeof action.payload.identifier === 'string' ? action.payload.identifier : identifierToKey(action.payload.identifier);

			let newState = action.payload.state;

			//To ensure clean functionality throughout the app, we will add post-processing functionality here.

			// Filters
			// Column filters should always have an **id**, used by tanstack, and a **key**, used by the backend.
			// Additionally, fallback filter functions should always be available, on the columns.
			//We should only do this if it's actually necessary.
			const needsFilterUpdate = newState.filterState && newState.filterState.some(filter => !filter.id || !filter.key);
			const needsColumnUpdate =
				newState.filterState &&
				newState.columnDefs &&
				newState.filterState.some(filter =>
				{
					if (filter.id === 'Select a column') return false;
					const targetColumn = newState.columnDefs.find((col: any) => col.accessorKey === filter.id);
					return !targetColumn || !targetColumn.filterFn;
				});

			// console.log('Needs filter update', needsFilterUpdate);
			// console.log('Needs column update', needsColumnUpdate);

			if (needsFilterUpdate || needsColumnUpdate)
			{
				let newColumnDefs = [...newState.columnDefs];
				//let newFilterState = newState.filterState.map(filter =>
				newState.filterState.forEach(filter =>
				{
					//let newFilter = { ...filter, id: filter.id || filter.key!, key: filter.key || filter.id! };
					if (!filter.id)
					{
						filter.id = filter.key!;
					}
					if (!filter.key)
					{
						filter.key = filter.id!;
					}

					// Add fallback filterFn if available
					let filterValue = filter.value;
					let filterColumn = filter.id;
					let filterOperator = filter.operator;
					let filterType = filter.type;

					if (filterColumn === 'Select a column') return;

					let filterFn: FilterFnOption<Row>;

					//For compound filters, we need to separate out the values
					if (filterType === 'compound')
					{
						filterFn = (row: Row, columnId: string, filterValue: any) =>
						{
							let match = false;
							try
							{
								const targetValues = Object.values(filterValue);
								const filterParts = targetValues.map((val: any) => val[0]);

								const rowValue = row.original[columnId];
								rowValue.forEach((val: any) =>
								{
									//Extract parts of the row value
									const parts = val.split(' - ');
									//console.log('Checking compound', filterParts, 'every in', parts);

									if (filterParts.every((part: any) => parts.includes(part)))
									{
										console.log('Matched compound', parts, filterParts);
										match = true;
										return;
									}
								});
							}
							catch (e)
							{
								console.error('Could not filter compound', e);
								return false;
							}

							return match;
						};
					}
					else if (filterOperator === 'eq' || filterOperator === 'equals')
					{
						//For dates, we want to check if just the day, month, and year are equal - ignoring timezones and hms
						if (filterType === 'date')
						{
							filterFn = (row: Row, columnId: string, filterValue: any) =>
							{
								const rowValue = row.original[columnId];
								const rowDate = new Date(rowValue);
								const filterDate = new Date(filterValue);

								//console.log('Checking date', rowValue, '->', rowDate, filterDate);
								return (
									rowDate.getDate() === filterDate.getDate() &&
									rowDate.getMonth() === filterDate.getMonth() &&
									rowDate.getFullYear() === filterDate.getFullYear()
								);
							};
						}
						else filterFn = 'equals';
					}
					else if (filterOperator === 'includes' || filterOperator === 'contains')
					{
						filterFn = (row: Row, columnId: string, filterValue: any) =>
						{
							console.log('Checking includes', filterValue, row.original[columnId]);

							let rowValue = row.original[columnId];

							//For evidence columns, we'll have an object with .value
							if (typeof rowValue === 'object' && rowValue.value)
							{
								rowValue = rowValue.value;
							}

							//For lists, they'll often be id/name matches
							if (filterType === 'list')
							{
								console.log('Checking list', filterValue, rowValue);
								if (Array.isArray(rowValue))
								{
									return rowValue.some((val: any) =>
									{
										//For now, this is fast and accurate enough
										return filterValue.some((fval: any) => JSON.stringify(val).includes(fval));
									});
								}
								else
								{
									return filterValue.some((fval: any) => JSON.stringify(rowValue).includes(fval));
								}
							}
							else if (typeof filterValue === 'string' && typeof rowValue === 'string')
							{
								return rowValue.toLowerCase().includes(filterValue.toLowerCase());
							}
							else if (typeof filterValue === 'string' && Array.isArray(rowValue))
							{
								return rowValue.some(val => val.toLowerCase().includes(filterValue.toLowerCase()));
							}
							else if (Array.isArray(filterValue) && typeof rowValue === 'string')
							{
								return filterValue.some(val => val.toLowerCase().includes(rowValue.toLowerCase()));
							}
							else if (Array.isArray(filterValue) && Array.isArray(rowValue))
							{
								return filterValue.some(val => rowValue.includes(val));
							}
							else
							{
								console.log('Could not filter', filterValue, rowValue);
								return false;
							}
						};
					}
					else if (filterOperator === 'gt' && filterType === 'date')
					{
						filterFn = (row: Row, columnId: string, filterValue: any) =>
						{
							const rowValue = row.original[columnId];
							const rowDate = new Date(rowValue);
							const filterDate = new Date(filterValue);

							console.log('Checking date', rowValue, '->', rowDate, filterDate);
							return rowDate > filterDate;
						};
					}
					else if (filterOperator === 'lt' && filterType === 'date')
					{
						filterFn = (row: Row, columnId: string, filterValue: any) =>
						{
							const rowValue = row.original[columnId];
							const rowDate = new Date(rowValue);
							const filterDate = new Date(filterValue);

							console.log('Checking date', rowValue, '->', rowDate, filterDate);
							return rowDate < filterDate;
						};
					}
					else if (filterOperator === 'gt')
					{
						filterFn = 'inNumberRange';
					}
					else if (filterOperator === 'lt')
					{
						filterFn = 'inNumberRange';
					}
					else
					{
						filterFn = 'equals';
					}

					// const column = newColumnDefs.find((col: any) => col.accessorKey === filterColumn);
					const columnIndex = newColumnDefs.findIndex((col: any) => col.accessorKey === filterColumn);
					if (columnIndex === -1)
					{
						console.error('Could not find column', filterColumn);
					}
					else
					{
						//column.filterFn = filterFn;
						// console.log('Assigning filterFn to column', filterColumn, filterFn);
						newColumnDefs[columnIndex] = {
							...newColumnDefs[columnIndex],
							filterFn: filterFn
						};
					}

					//return newFilter;
				});

				newState.columnDefs = newColumnDefs;
				//newState.filterState = newFilterState;
			}

			state.datagrids[key] = newState; //action.payload.state;
		}
	}
});

export type { DatagridState };
export const { setDatagrid } = datagridSlice.actions;
export default datagridSlice.reducer;
